import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { supabase } from "../../config/supabase";
import AnimatedOverpixelLogo from "../AnimatedOverpixelLogo";
import LoadingAnimation from "../LoadingAnimation";
import { FaWhatsapp, FaUserCircle, FaBolt } from "react-icons/fa";

type OnboardingForm = {
  display_name: string;
  phone_number: string;
  company_name: string;
  job_role: string;
  social_handle: string;
  birth_date: string; // ISO date (YYYY-MM-DD)
};

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  userUid: string;
  userEmail?: string | null;
  initialData?: Partial<OnboardingForm> & {
    onboarding_bonus_awarded?: boolean;
  };
}

// Simple blocking modal using Tailwind classes already used in project
export default function OnboardingModal({
  isOpen,
  onClose,
  userUid,
  userEmail,
  initialData,
}: OnboardingModalProps) {
  const [form, setForm] = useState<OnboardingForm>({
    display_name: initialData?.display_name ?? "",
    phone_number: initialData?.phone_number ?? "",
    company_name: initialData?.company_name ?? "",
    job_role: initialData?.job_role ?? "",
    social_handle: initialData?.social_handle ?? "",
    birth_date: initialData?.birth_date ?? "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [birthDateBR, setBirthDateBR] = useState<string>("");
  const [step, setStep] = useState<1 | 2>(1);
  const [showIntro, setShowIntro] = useState(true);
  const [completed, setCompleted] = useState(false);

  // Autoformatação simples para telefone BR: (XX) XXXXX-XXXX
  const formatPhoneBR = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 11);
    const ddd = digits.slice(0, 2);
    const first = digits.slice(2, 7);
    const last = digits.slice(7, 11);
    if (digits.length <= 2) return ddd ? `(${ddd}` : "";
    if (digits.length <= 7) return `(${ddd}) ${first}`;
    return `(${ddd}) ${first}-${last}`;
  };

  // Nome de boas-vindas dinâmico
  const welcomeName = useMemo(() => {
    const base = (form.display_name || initialData?.display_name || (userEmail?.split("@")[0] ?? "")).trim();
    return base && base.length > 0 ? base : "OverPixel";
  }, [form.display_name, initialData?.display_name, userEmail]);

  useEffect(() => {
    if (initialData) {
      setForm((prev) => ({
        ...prev,
        display_name: initialData.display_name ?? prev.display_name,
        phone_number: initialData.phone_number ? formatPhoneBR(initialData.phone_number) : prev.phone_number,
        company_name: initialData.company_name ?? prev.company_name,
        job_role: initialData.job_role ?? prev.job_role,
        social_handle: initialData.social_handle ?? prev.social_handle,
        birth_date: initialData.birth_date ?? prev.birth_date,
      }));
      // Inicializa exibição BR se houver ISO
      if (initialData.birth_date) {
        setBirthDateBR(isoToBR(initialData.birth_date));
      }
    }
  }, [initialData]);

  // Controla loader/intro e reseta etapas na abertura do modal
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setCompleted(false);
      setShowIntro(true);
      const t = setTimeout(() => setShowIntro(false), 1200);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  const isValid = useMemo(() => {
    // Required: display_name, phone_number
    const nameOk = form.display_name.trim().length >= 2;
    const phoneOk = /^\(\d{2}\)\s\d{4,5}-\d{4}$/.test(form.phone_number.trim());
    return nameOk && phoneOk;
  }, [form.display_name, form.phone_number]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    if (name === "phone_number") {
      const formatted = formatPhoneBR(value);
      setForm((prev) => ({ ...prev, [name]: formatted }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Utilidades de data em formato BR (dd/mm/aaaa)
  const isoToBR = (iso?: string | null) => {
    if (!iso) return "";
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return "";
    const yyyy = m[1];
    const mm = m[2];
    const dd = m[3];
    return `${dd}/${mm}/${yyyy}`;
  };

  const brToISO = (br: string) => {
    const m = br.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return "";
    const dd = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    const yyyy = parseInt(m[3], 10);
    if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return "";
    const d = new Date(yyyy, mm - 1, dd);
    // validações de data real
    if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return "";
    return `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
  };

  const handleBirthDateChange = (value: string) => {
    // máscara simples dd/mm/aaaa
    let v = value.replace(/\D/g, "");
    v = v.slice(0, 8);
    let out = v;
    if (v.length > 4) out = `${v.slice(0, 2)}/${v.slice(2, 4)}/${v.slice(4)}`;
    else if (v.length > 2) out = `${v.slice(0, 2)}/${v.slice(2)}`;
    setBirthDateBR(out);
    const iso = brToISO(out);
    setForm((prev) => ({ ...prev, birth_date: iso }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // 1) Update profile fields + mark onboarding_completed = true
      const { error: updateErr } = await supabase
        .from("users")
        .update({
          display_name: form.display_name,
          phone_number: form.phone_number,
          company_name: form.company_name || null,
          job_role: form.job_role || null,
          social_handle: form.social_handle || null,
          birth_date: form.birth_date || null,
          onboarding_completed: true,
          updated_at: new Date().toISOString(),
        })
        .eq("uid", userUid);

      if (updateErr) throw updateErr;

      // 2) Fetch flags to decide bonus
      const { data: userRow, error: fetchErr } = await supabase
        .from("users")
        .select("onboarding_bonus_awarded, token_balance, email")
        .eq("uid", userUid)
        .single();

      if (fetchErr) throw fetchErr;

      const alreadyAwarded = Boolean(
        initialData?.onboarding_bonus_awarded ?? userRow?.onboarding_bonus_awarded
      );

      // 3) Award +20 tokens if not yet awarded
      if (!alreadyAwarded) {
        // Try RPC first; if it fails, fall back to direct update
        let bonusApplied = false;
        try {
          const { error: rpcErr } = await supabase.rpc(
            "tokens_transfer_atomic_ledger",
            {
              p_user_uid: userUid,
              p_amount: 20,
              p_reason: "onboarding_bonus",
            }
          );
          if (rpcErr) throw rpcErr;
          bonusApplied = true;
        } catch (rpcError) {
          // Fallback: direct balance update (not ideal, but ensures UX continuity)
          const currentBalance = Number(userRow?.token_balance ?? 0);
          const { error: balErr } = await supabase
            .from("users")
            .update({ token_balance: currentBalance + 20 })
            .eq("uid", userUid);
          if (balErr) throw balErr;
          bonusApplied = true;
        }

        if (bonusApplied) {
          // Flag as awarded to prevent duplicates
          const { error: flagErr } = await supabase
            .from("users")
            .update({ onboarding_bonus_awarded: true })
            .eq("uid", userUid);
          if (flagErr) throw flagErr;
        }
      }

      // Concluído: mostrar tela final e CTA, sem banners sobrepostos
      setCompleted(true);
      setSuccess(null);
    } catch (err: any) {
      setError(err?.message ?? "Falha ao salvar seu perfil. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-gradient-to-br from-cyber-black/90 via-black/80 to-cyber-black/90 backdrop-blur-sm">
      {/* Intro loader com pixels azuis (rápido) */}
      {showIntro && (
        <div className="absolute inset-0 flex items-center justify-center">
          <LoadingAnimation />
        </div>
      )}
      <div className="relative w-full max-w-lg rounded-2xl border border-cyan-400/40 bg-cyber-gray-dark/70 backdrop-blur-xl p-6 shadow-neon-blue neon-glow overflow-y-auto" style={{ maxHeight: "80vh" }}>
        {/* Logo aterrissando */}
        <AnimatePresence>
          {!showIntro && (
            <motion.div
              initial={{ y: -120, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -150, opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", stiffness: 120, damping: 16 }}
              className="w-full mb-4"
            >
              {/* Wrapper garante centralização absoluta e deslocamento fino */}
              <div className="grid place-items-center ml-[5px]">
                <AnimatedOverpixelLogo size="md" glowIntensity="high" showParticles={true} minimal={true} tilt={false} showText={false} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mensagens de boas-vindas + conteúdo inline pós-conclusão */}
        {!showIntro && (
          <>
            <h2 className="mb-3 text-2xl font-cyber text-white text-center">
              <span className="font-extrabold text-cyber-blue neon-text">Seja Bem Vindo(a), {welcomeName}!</span>
            </h2>
            {completed ? (
              <div className="mb-4 mx-auto max-w-md rounded-xl bg-gradient-to-br from-green-900/40 to-emerald-900/20 border border-green-500/30 backdrop-blur-md p-6 text-sm text-white/90 shadow-[0_0_25px_rgba(34,197,94,0.2)]">
                <div className="flex flex-col items-center text-center mb-4">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-3 border border-green-500/40 shadow-inner">
                    <FaBolt className="text-3xl text-green-400" />
                  </div>
                  <p className="font-bold text-xl text-green-300 mb-1">Cadastro Concluído!</p>
                  <p className="text-white/80">Você acabou de ganhar <span className="text-yellow-400 font-bold">20 OverTokens</span>.</p>
                </div>
                
                <div className="bg-black/30 rounded-lg p-3 mb-4 border border-white/5">
                  <p className="mb-2 text-center text-xs text-white/60 uppercase tracking-wider font-semibold">Próximo Passo</p>
                  <p className="text-center text-sm">
                    Convide amigos e desbloqueie ainda mais recompensas ⚡. <br/>
                    <span className="text-cyan-300 font-bold">+50 Tokens</span> por indicação!
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  <button
                    type="button"
                    className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white font-semibold hover:bg-white/10 hover:border-white/20 transition-all flex items-center justify-center gap-2 group"
                    onClick={() => {
                      const link = `https://overpixel.web.app/invite/${userUid}`;
                      try { 
                        navigator.clipboard.writeText(link);
                        setSuccess("Link copiado!");
                        setTimeout(() => setSuccess(null), 2000);
                      } catch {}
                    }}
                  >
                    <span>Copiar Link</span>
                    <span className="bg-white/10 rounded px-1.5 py-0.5 text-xs group-hover:bg-white/20 transition-colors">Convite</span>
                  </button>
                  <button
                    type="button"
                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
                    onClick={() => onClose()}
                  >
                    <span>Acessar Plataforma</span>
                    <FaBolt />
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Subtítulo otimizado + pill suave para tokens */}
                <div className="mb-6 text-center leading-relaxed">
                  <p className="text-base text-white/80 font-light">
                    Complete seu perfil agora e receba
                    <span className="inline-flex items-center gap-1.5 mx-2 px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/40 text-yellow-300 font-bold shadow-[0_0_15px_rgba(234,179,8,0.2)] transform hover:scale-105 transition-transform cursor-default">
                      <FaBolt className="text-yellow-400" /> +20 OverTokens
                    </span>
                    <span className="block mt-2 text-sm text-white/50">
                      Use-os para criar estampas, gerar imagens e usar ferramentas IA.
                    </span>
                  </p>
                </div>

                {/* Indicador de progresso leve */}
                <div className="mb-6 flex justify-center">
                  <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
                    <div className={`h-2 w-2 rounded-full ${step === 1 ? "bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.6)]" : "bg-white/20"}`} />
                    <div className={`h-2 w-2 rounded-full ${step === 2 ? "bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.6)]" : "bg-white/20"}`} />
                    <span className="ml-2 text-xs font-medium text-white/60">
                      {step === 1 ? "Dados Básicos" : "Personalização"}
                    </span>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {error && (
          <div className="mb-3 rounded bg-red-50 p-2 text-sm text-red-600">{error}</div>
        )}
        {success && !completed && (
          <div className="mb-3 rounded bg-green-50 p-2 text-sm text-green-700">{success}</div>
        )}

        <form onSubmit={handleSubmit} className={completed ? "hidden" : "space-y-4"} aria-hidden={completed}
        >
          <div>
            <label className="mb-1 block text-sm font-cyber text-white/90">Nome exibido</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-cyber-blue">
                <FaUserCircle />
              </span>
              <input
                type="text"
                name="display_name"
                value={form.display_name}
                onChange={handleChange}
                className="w-full rounded-lg border-transparent ring-1 ring-cyan-300/25 bg-cyber-black/50 backdrop-blur-sm p-2 pl-9 text-white placeholder-white/40 focus:ring-2 focus:ring-cyan-300/60 focus:border-transparent shadow-inner"
                placeholder="Seu nome"
                required
              />
            </div>
            <p className="mt-1 text-xs text-white/50">Esse será o nome visível no seu perfil.</p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-cyber text-white/90">WhatsApp</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-green-400">
                <FaWhatsapp />
              </span>
              <input
                type="tel"
                name="phone_number"
                value={form.phone_number}
                onChange={handleChange}
                className="w-full rounded-lg border-transparent ring-1 ring-cyan-300/25 bg-cyber-black/50 backdrop-blur-sm p-2 pl-9 text-white placeholder-white/40 focus:ring-2 focus:ring-cyan-300/60 focus:border-transparent shadow-inner"
                placeholder="(11) 99999-9999"
                required
              />
            </div>
            <p className="mt-1 text-xs text-white/50">Seu número é usado apenas para benefícios e suporte — nunca será público.</p>
          </div>

          {/* Etapa 2: campos opcionais aparecem só após avançar */}
          {step === 2 && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-cyber text-white/90">Empresa</label>
              <input
                type="text"
                name="company_name"
                value={form.company_name}
                onChange={handleChange}
                className="w-full rounded-lg border border-cyber-blue/40 bg-cyber-black/60 p-2 text-white placeholder-white/40 focus:border-cyber-blue focus:ring-2 focus:ring-cyber-blue"
                placeholder="Opcional"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-cyber text-white/90">Cargo</label>
              <input
                type="text"
                name="job_role"
                value={form.job_role}
                onChange={handleChange}
                className="w-full rounded-lg border border-cyber-blue/40 bg-cyber-black/60 p-2 text-white placeholder-white/40 focus:border-cyber-blue focus:ring-2 focus:ring-cyber-blue"
                placeholder="Opcional"
              />
            </div>
          </div>
          )}

          {step === 2 && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-cyber text-white/90">@ Social</label>
              <input
                type="text"
                name="social_handle"
                value={form.social_handle}
                onChange={handleChange}
                className="w-full rounded-lg border border-cyber-blue/40 bg-cyber-black/60 p-2 text-white placeholder-white/40 focus:border-cyber-blue focus:ring-2 focus:ring-cyber-blue"
                placeholder="Opcional (@seuuser)"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-cyber text-white/90">Data de nascimento</label>
              <input
                type="text"
                inputMode="numeric"
                name="birth_date_br"
                value={birthDateBR}
                onChange={(e) => handleBirthDateChange(e.target.value)}
                placeholder="dd/mm/aaaa"
                pattern="\d{2}/\d{2}/\d{4}"
                className="w-full rounded-lg border-transparent ring-1 ring-cyan-300/25 bg-cyber-black/50 backdrop-blur-sm p-2 text-white placeholder-white/40 focus:ring-2 focus:ring-cyan-300/60 focus:border-transparent shadow-inner"
                aria-label="Data de nascimento no formato dia/mês/ano"
              />
            </div>
          </div>
          )}

          {/* Área de botões por etapa */}
          {step === 1 ? (
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-sm font-cyber bg-transparent border border-cyan-400/30 text-cyan-300/60 transition-all hover:border-cyan-400 hover:text-cyan-300 hover:shadow-neon-blue"
                onClick={onClose}
                disabled={isSubmitting}
                title="Cancelar"
              >
                Cancelar
              </button>
              <motion.button
                type="button"
                onClick={() => setStep(2)}
                className="rounded-xl px-5 py-2 text-sm font-cyber bg-gradient-to-r from-cyan-300 to-fuchsia-400 text-cyber-black shadow-neon-blue transition-all neon-button"
                disabled={!isValid}
                animate={isValid ? { boxShadow: "0 0 28px rgba(6,182,212,0.55)", scale: 1.02 } : { boxShadow: "0 0 0 rgba(0,0,0,0)", scale: 1 }}
                whileHover={{ scale: 1.04 }}
              >
                Próximo
              </motion.button>
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center gap-3 w-full">
              <motion.button
                type="submit"
                className="w-full rounded-2xl px-6 py-3 text-lg font-extrabold font-cyber bg-gradient-to-r from-cyber-blue via-cyber-green to-cyan-500 text-cyber-black shadow-neon-blue transition-all neon-button"
                disabled={isSubmitting}
                animate={isValid ? { boxShadow: "0 0 30px rgba(99,102,241,0.55)", scale: 1.01 } : { boxShadow: "0 0 0 rgba(0,0,0,0)", scale: 1 }}
                whileHover={{ scale: 1.03 }}
              >
                ✨ Concluir e Ganhar Tokens
              </motion.button>
            </div>
          )}
        </form>

        {/* Tela final inline já aparece acima da saudação; removido segundo card duplicado. */}

      </div>
    </div>
  );
}
